// src/App.js

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Route, Routes, useLocation, Navigate, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import GlobalStyles from '@mui/material/GlobalStyles';
import CircularProgress from '@mui/material/CircularProgress';

import Login from './Container/Login/login';
import Signup from './Container/Signup/signup';
import VerifyEmail from './Container/VerifyEmail/verifyEmail';
import Dashboard from './Container/Dashboard/dashboard';
import ChangePassword from './Container/ChangePassword/ChangePassword';

import CustomDrawer from './Components/Drawer/CustomDrawer/CustomDrawer';
import ViewInfo from './Components/ViewInfo/ViewInfo';
import Home from './Components/Home/Home';
import FullscreenPage from './Components/Home/FullscreenPage';
import ViewInfoFullPage from './Components/ViewInfo/ViewInfoFullPage';
import TopBar from './Components/TopBar/TopBar';
import AdminPanel from './Components/Admin/AdminPanel';
import ImportMarkersPanel from './Components/ImportMarkers/ImportMarkersPanel';
import PromoPanel from './Components/Promo/PromoPanel';
import NewsCarousel from './Components/Home/NewsCarousel';
import NewsfeedDownload from './Components/Promo/NewsfeedDownload';

import FilterPanel from './Components/Filter/FilterPanel';
import AlertBox from './Components/Alert/alert';
import ConfirmDialog from './Components/ConfirmDialog/ConfirmDialog';

import { MapProvider, useMapState } from './Contexts/MapContext';
import { IconProvider } from './Contexts/IconContext';
import { useSearchConversion } from './Hooks/useSearchConversion';

import { ThemeProvider } from '@mui/material/styles';
import { getAppTheme } from './theme';

import { smoothFlyTo } from './Components/GoogleMap/CameraAnimator';
import MainBg from './assets/images/main-background.png';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { auth, db } from './Firebase/firebase';

const HEADER_HEIGHT = '96px';
const DRAWER_WIDTH = '400px';

const INITIAL_DRAWING_STYLES = {
  polygonColor: '#FF9900',
  lineColor: '#00B0FF',
  lineWeight: 4,
  markerIcon: null,
  iconID: null,
  hoverImage: null,
  hoverContent: '',
};

function SearchConversionHandler({ open, onClose, searchData, onSuccess, onError }) {
  const { convertSearchToMarker } = useSearchConversion();

  const handleConfirm = async () => {
    if (!searchData) return;
    try {
      const savedMarker = await convertSearchToMarker(searchData);
      onClose();
      if (onSuccess) onSuccess(savedMarker);
    } catch (e) {
      console.error(e);
      onClose();
      if (onError) onError();
    }
  };

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={handleConfirm}
      message='Convert to default marker?'
      severity='info'
      confirmText='Yes'
      cancelText='No'
      sx={{ zIndex: 100005 }}
    />
  );
}

function InnerApp() {
  const location = useLocation();
  const navigate = useNavigate();

  const isMapRoute = [
    '/',
    '/signin',
    '/signup',
    '/verify-email',
    '/change-password',
    '/fullscreen',
    '/info-fullscreen',
    '/download-newsfeed',
  ].includes(location.pathname);

  const isFullscreenRoute = location.pathname === '/fullscreen';
  const isInfoFullscreenRoute = location.pathname === '/info-fullscreen';
  const isDownloadRoute = location.pathname === '/download-newsfeed';

  const [authLoading, setAuthLoading] = useState(true);

  // --- MODAL AUTH STATE ---
  const [authModal, setAuthModal] = useState(null);
  const [linkingData, setLinkingData] = useState(null);

  // --- GLOBAL APP STATE ---
  const [mainLogoUrl, setMainLogoUrl] = useState('');
  const [makeUppercase, setMakeUppercase] = useState(false);

  const [isUIDirty, setIsUIDirty] = useState(false);
  const [selectedMarkerData, setSelectedMarkerData] = useState(null);
  const [isDrawerEditing, setIsDrawerEditing] = useState(false);
  const [activeMenu, setActiveMenu] = useState('HOME');
  const [activeTool, setActiveTool] = useState('MARKER');

  const { resetMap } = useMapState();

  const [closeTrigger, setCloseTrigger] = useState(0);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showPromo, setShowPromo] = useState(false);

  const [showSearchConvertDialog, setShowSearchConvertDialog] = useState(false);
  const [tempSearchData, setTempSearchData] = useState(null);

  const [getMapCurrentState, setGetMapCurrentState] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  const [filterCriteria, setFilterCriteria] = useState(null);
  const [searchMarker, setSearchMarker] = useState(null);
  const [currentAction, setCurrentAction] = useState({
    type: null,
    payload: null,
    id: 0,
  });
  const [alert, setAlert] = useState({ visible: false, severity: 'info', message: '' });

  const [drawingStyles, setDrawingStyles] = useState(INITIAL_DRAWING_STYLES);

  const [showPost, setShowPost] = useState(false);
  const [editPost, setEditPost] = useState(false);
  const [eventDetails, setEventsDetails] = useState({
    id: '',
    name: '',
    place: '',
    address: '',
    time: '',
  });

  const pendingNavRef = useRef(null);
  const lastSelectionTimeRef = useRef(0);
  const selectionLockRef = useRef(false);
  const selectionLockTimeoutRef = useRef(null);

  const mapRef = useRef(null);
  const pendingConfigRef = useRef(null);

  // Fetch Main Logo globally
  useEffect(() => {
    let isMounted = true;
    const fetchLogo = async () => {
      try {
        const logoRef = doc(db, 'settings', 'MainLogo');
        const logoSnap = await getDoc(logoRef);
        if (logoSnap.exists() && isMounted) {
          const data = logoSnap.data();
          if (data.url) {
            setMainLogoUrl(data.url);
          }
        }
      } catch (error) {
        console.error('Error fetching MainLogo:', error);
      }
    };

    fetchLogo();

    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch TextFormatting Setting dynamically
  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'settings', 'TextFormatting'),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setMakeUppercase(data.makeUppercase === true || data.makeUppercase === 'true');
        } else {
          setMakeUppercase(false);
        }
      },
      (error) => console.error('Error fetching TextFormatting settings:', error),
    );
    return () => unsubscribe();
  }, []);

  // Generate dynamic theme exclusively for specific modules mapped to TextFormatting
  const importModuleTheme = useMemo(() => getAppTheme(makeUppercase), [makeUppercase]);

  useEffect(() => {
    const path = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    const oobCode = searchParams.get('oobCode');
    const mode = searchParams.get('mode');

    if (path === '/signin') {
      setAuthModal('LOGIN');
    } else if (path === '/signup') {
      setAuthModal('SIGNUP');
    } else if (path === '/verify-email') {
      setAuthModal('VERIFY');
    } else if (oobCode) {
      if (mode === 'resetPassword') setAuthModal('LOGIN');
      else setAuthModal('VERIFY');
    } else {
      setAuthModal(null);
    }
  }, [location.pathname, location.search]);

  const handleAuthModalClose = () => {
    setAuthModal(null);
    setLinkingData(null);
    navigate('/', { replace: true });
  };

  useEffect(() => {
    if (!authLoading && !auth.currentUser && location.pathname === '/change-password') {
      navigate('/signin', { replace: true });
    }
  }, [authLoading, location.pathname, navigate]);

  const executeWithATC = useCallback((tLat, tLng, executeCallback) => {
    executeCallback();
  }, []);

  const handleResetMap = useCallback(
    (config) => {
      if (!config || !config.center) return;

      if (!mapRef.current) {
        pendingConfigRef.current = config;
        return;
      }

      const tLat =
        typeof config.center.lat === 'function' ? config.center.lat() : config.center.lat;
      const tLng =
        typeof config.center.lng === 'function' ? config.center.lng() : config.center.lng;
      const tZoom = config.zoom;

      const executeFlight = () => {
        if (tLat !== undefined && tLng !== undefined) {
          setSearchMarker(null);
          smoothFlyTo(mapRef.current, tLat, tLng, tZoom);
        }
      };

      executeWithATC(tLat, tLng, executeFlight);
    },
    [executeWithATC],
  );

  const handleSetSearchMarker = useCallback(
    (marker) => {
      if (!marker) {
        setSearchMarker(null);
        return;
      }

      let tLat, tLng;
      if (marker.geometry?.location) {
        tLat =
          typeof marker.geometry.location.lat === 'function'
            ? marker.geometry.location.lat()
            : marker.geometry.location.lat;
        tLng =
          typeof marker.geometry.location.lng === 'function'
            ? marker.geometry.location.lng()
            : marker.geometry.location.lng;
      } else if (marker.position) {
        tLat =
          typeof marker.position.lat === 'function'
            ? marker.position.lat()
            : marker.position.lat;
        tLng =
          typeof marker.position.lng === 'function'
            ? marker.position.lng()
            : marker.position.lng;
      } else if (marker.lat !== undefined && marker.lng !== undefined) {
        tLat = marker.lat;
        tLng = marker.lng;
      }

      // STRICT TYPE SAFETY FOR GOOGLE MAPS
      // Convert database strings (e.g. "-34.02") into absolute numbers.
      const safeLat = parseFloat(tLat);
      const safeLng = parseFloat(tLng);

      if (isNaN(safeLat) || isNaN(safeLng)) {
        console.warn('handleSetSearchMarker: Invalid coordinates detected', marker);
        return;
      }

      const executeSearch = () => {
        // Guarantee the object we pass to Google Maps has strict numeric coordinates
        setSearchMarker({
          ...marker,
          lat: safeLat,
          lng: safeLng,
          position: { lat: safeLat, lng: safeLng },
        });
      };

      executeWithATC(safeLat, safeLng, executeSearch);
    },
    [executeWithATC],
  );

  const handleMapLoad = (mapInstance) => {
    mapRef.current = mapInstance;
    if (pendingConfigRef.current) {
      handleResetMap(pendingConfigRef.current);
      pendingConfigRef.current = null;
    }
  };

  const handleResetFilter = () => {
    setFilterCriteria(null);
    setShowFilter(false);
  };

  const handleSetSelectedCategory = (category) => {
    if (!category) {
      setFilterCriteria(null);
    } else {
      setFilterCriteria({ categoryIds: [category] });
    }
  };

  useEffect(() => {
    document.body.style.backgroundImage = `url(${MainBg})`;
    document.body.style.backgroundSize = 'cover';
    document.body.style.backgroundRepeat = 'no-repeat';
    document.body.style.backgroundPosition = 'center';
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setAuthLoading(false);
    });
    return () => unsub();
  }, []);

  const showAlert = useCallback((severity, message) => {
    setAlert({ visible: true, severity, message });
    setTimeout(() => {
      setAlert({ visible: false, severity: '', message: '' });
    }, 3000);
  }, []);

  const handleAction = useCallback(
    (actionType, payload = null) => {
      if (actionType === 'EDIT' && currentAction?.type === 'ADD_NEW' && isUIDirty) {
        return;
      }

      const finalPayload =
        actionType === 'DELETE' && !payload ? selectedMarkerData : payload;

      setCurrentAction({ type: actionType, payload: finalPayload, id: Date.now() });

      if (actionType === 'ADD_NEW') {
        setIsUIDirty(true);
        if (!payload?.preserveDrafts) {
          setSelectedMarkerData(null);
          if (resetMap) resetMap();
          setDrawingStyles(INITIAL_DRAWING_STYLES);
        }
      }

      if (actionType === 'ABORT') {
        pendingNavRef.current = null;
        return;
      }

      if (['CLOSE', 'CANCEL', 'CLOSE_DRAWER', 'DELETE', 'SAVE'].includes(actionType)) {
        setIsUIDirty(false);

        if (resetMap) resetMap();

        if (actionType !== 'SAVE') {
          setSelectedMarkerData(null);

          setDrawingStyles((prev) => ({
            ...INITIAL_DRAWING_STYLES,
            polygonColor: prev.polygonColor,
            lineColor: prev.lineColor,
            lineWeight: prev.lineWeight,
          }));
        }

        if (pendingNavRef.current) {
          const { type, target } = pendingNavRef.current;
          pendingNavRef.current = null;

          setTimeout(() => {
            if (type === 'MENU') {
              setActiveMenu(target);
            } else if (type === 'ADMIN') {
              setShowAdmin(target);
            } else if (type === 'PROMO') {
              setShowPromo(target);
            } else if (type === 'SELECTION') {
              lastSelectionTimeRef.current = Date.now();
              selectionLockRef.current = true;
              if (selectionLockTimeoutRef.current)
                clearTimeout(selectionLockTimeoutRef.current);
              selectionLockTimeoutRef.current = setTimeout(() => {
                selectionLockRef.current = false;
              }, 500);

              if (target) {
                const cleanTarget = {
                  ...target,
                  hoverImage: target.hoverImage || null,
                  hoverContent: target.hoverContent || '',
                };
                setSelectedMarkerData(cleanTarget);
                setCurrentAction({
                  type: 'FORCE_SELECT',
                  payload: cleanTarget,
                  id: Date.now(),
                });
              } else {
                setSelectedMarkerData(null);
              }
            }
          }, 100);
        }
      }

      if (actionType === 'DELETE') {
        showAlert('success', 'Item deleted successfully');
      }
    },
    [showAlert, selectedMarkerData, resetMap, currentAction, isUIDirty],
  );

  const handleSetActiveMenu = useCallback(
    (menu) => {
      const isUpdateContext =
        activeMenu === 'MARKUP_MARKERS' || activeMenu === 'MARKUP_INFO';

      if (isUpdateContext && menu !== activeMenu && isUIDirty) {
        pendingNavRef.current = { type: 'MENU', target: menu };
        handleAction('CANCEL');
        return;
      }

      if (menu !== activeMenu) {
        setSelectedMarkerData(null);

        setDrawingStyles((prev) => ({
          ...INITIAL_DRAWING_STYLES,
          polygonColor: prev.polygonColor,
          lineColor: prev.lineColor,
          lineWeight: prev.lineWeight,
        }));

        handleAction('CLOSE_DRAWER');
      }

      setActiveMenu(menu);
    },
    [activeMenu, isUIDirty, handleAction],
  );

  const handleFilterResults = useCallback(
    (count) => {
      showAlert('success', `Filter Applied: Found ${count} location(s).`);
    },
    [showAlert],
  );

  const updateMarkerInState = (updatedMarker) => {
    setSelectedMarkerData(updatedMarker);
  };

  const handleMarkerSelectionRequest = useCallback(
    (newData) => {
      setSelectedMarkerData((prevData) => {
        let resolvedNewData = typeof newData === 'function' ? newData(prevData) : newData;

        if (
          isUIDirty &&
          currentAction?.type !== 'ADD_NEW' &&
          currentAction?.type !== 'FORCE_SELECT'
        ) {
          const isSameAsSelected =
            prevData && resolvedNewData && prevData.id === resolvedNewData.id;
          const isNewlyCreated = resolvedNewData && resolvedNewData.isNew === true;

          const currentGroupId = prevData?.markerGroupID || prevData?.id;
          const newGroupId = resolvedNewData?.markerGroupID || resolvedNewData?.id;
          const isSameGroup =
            currentGroupId && newGroupId && String(currentGroupId) === String(newGroupId);

          if (!isSameAsSelected && !isNewlyCreated && !isSameGroup) {
            pendingNavRef.current = { type: 'SELECTION', target: resolvedNewData };
            setTimeout(() => setCloseTrigger((prev) => prev + 1), 0);
            return prevData;
          }
        }

        if (resolvedNewData) {
          if (
            typeof resolvedNewData.id === 'string' &&
            resolvedNewData.id.startsWith('search-result-')
          ) {
            setTempSearchData(resolvedNewData);
            setTimeout(() => setShowSearchConvertDialog(true), 0);
            return prevData;
          }

          let finalData = resolvedNewData;

          if (currentAction?.type === 'ADD_NEW') {
            finalData = {
              ...resolvedNewData,
              isNew: true,
              markerGroupID:
                currentAction.payload?.markerGroupID || resolvedNewData.markerGroupID,
            };
          }

          lastSelectionTimeRef.current = Date.now();
          selectionLockRef.current = true;
          if (selectionLockTimeoutRef.current)
            clearTimeout(selectionLockTimeoutRef.current);
          selectionLockTimeoutRef.current = setTimeout(() => {
            selectionLockRef.current = false;
          }, 500);

          return finalData;
        } else {
          if (selectionLockRef.current) return prevData;
          return null;
        }
      });
    },
    [isUIDirty, currentAction],
  );

  const handleSelectionBlocked = useCallback((pendingData = null) => {
    if (pendingData) {
      pendingNavRef.current = { type: 'SELECTION', target: pendingData };
    }
    setCloseTrigger((prev) => prev + 1);
  }, []);

  const handleConvertSearchNo = () => {
    setShowSearchConvertDialog(false);
    setTempSearchData(null);
    setSearchMarker(null);
  };

  const handleConversionSuccess = (savedMarker) => {
    setTempSearchData(null);
    setSearchMarker(null);
    if (resetMap) resetMap();
    setIsUIDirty(false);
    setSelectedMarkerData(null);
    setCurrentAction({ type: 'CLOSE_DRAWER', payload: null, id: Date.now() });
    showAlert('success', 'Marker created successfully');
  };

  const isFullWidthMenu =
    activeMenu === 'HOME' ||
    activeMenu === 'NEWS' ||
    activeMenu === 'VIEW_INFO' ||
    ['MARKUP', 'MARKUP_MARKERS', 'MARKUP_INFO', 'IMPORT_MARKERS'].includes(activeMenu);

  const gridTemplateAreas = isFullWidthMenu
    ? '"header" "content"'
    : '"header header" "sidebar content"';

  const gridTemplateRows = `${HEADER_HEIGHT} 1fr`;

  const gridTemplateColumns = isFullWidthMenu ? '1fr' : `${DRAWER_WIDTH} 1fr`;

  const isDashboardEditing = ['HOME', 'VIEW_INFO', 'NEWS'].includes(activeMenu)
    ? false
    : isDrawerEditing;

  if (authLoading) {
    return (
      <Box
        sx={{
          height: '100dvh',
          width: '100vw',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#f4f4f4',
        }}>
        <CircularProgress size={50} sx={{ color: '#00B0FF' }} />
      </Box>
    );
  }

  if (isDownloadRoute) {
    return (
      <Box sx={{ backgroundColor: '#fff', width: '100vw', height: '100dvh' }}>
        <NewsfeedDownload logoUrl={mainLogoUrl} />
      </Box>
    );
  }

  return (
    <>
      <GlobalStyles
        styles={{
          html: {
            margin: 0,
            padding: 0,
            height: '100dvh',
            overflow: 'hidden',
            overscrollBehavior: 'none',
            boxSizing: 'border-box',
          },
          body: {
            margin: 0,
            padding: 0,
            height: '100dvh',
            overflow: 'hidden',
            overscrollBehavior: 'none',
          },
          '*, *::before, *::after': {
            boxSizing: 'inherit',
          },
          '#root': { height: '100dvh', width: '100vw' },

          ...(isUIDirty &&
            currentAction?.type === 'ADD_NEW' && {
              '#map, #map *, .gm-style, .gm-style *, .gm-style div': {
                cursor: 'crosshair !important',
              },
            }),
        }}
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateAreas: gridTemplateAreas,
          gridTemplateRows: gridTemplateRows,
          gridTemplateColumns: gridTemplateColumns,
          height: '100dvh',
          width: '100vw',
          overflow: 'hidden',
        }}>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255,255,255,0.75)',
            zIndex: 0,
            pointerEvents: 'none',
          }}
        />

        <Box sx={{ gridArea: 'header', zIndex: 1500, position: 'relative' }}>
          <TopBar
            activeMenu={activeMenu}
            setShowPost={setShowPost}
            setEventsDetails={setEventsDetails}
            setEditPost={setEditPost}
            setActiveMenu={handleSetActiveMenu}
            setShowAdmin={(val) => {
              if (val === true) {
                setSelectedMarkerData(null);
                setDrawingStyles(INITIAL_DRAWING_STYLES);
                handleAction('CLOSE_DRAWER');
                if (isUIDirty) {
                  pendingNavRef.current = { type: 'ADMIN', target: true };
                  handleAction('CANCEL');
                  return;
                }
              }
              setShowAdmin(val);
            }}
            setShowPromo={(val) => {
              if (val === true) {
                setSelectedMarkerData(null);
                setDrawingStyles(INITIAL_DRAWING_STYLES);
                handleAction('CLOSE_DRAWER');
                if (isUIDirty) {
                  pendingNavRef.current = { type: 'PROMO', target: true };
                  handleAction('CANCEL');
                  return;
                }
              }
              setShowPromo(val);
            }}
            isLocked={isUIDirty}
            resetMap={handleResetMap}
            setShowFilter={setShowFilter}
            resetFilter={handleResetFilter}
            setSearchMarker={handleSetSearchMarker}
            setSelectedCategory={handleSetSelectedCategory}
          />
        </Box>

        {showAdmin && (
          <AdminPanel
            onClose={() => setShowAdmin(false)}
            getMapCurrentState={getMapCurrentState}
          />
        )}
        {showPromo && <PromoPanel onClose={() => setShowPromo(false)} />}
        {showFilter && (
          <FilterPanel
            onClose={() => setShowFilter(false)}
            onApplyFilter={(criteria) => setFilterCriteria(criteria)}
            initialFilterState={filterCriteria}
          />
        )}
        {activeMenu === 'IMPORT_MARKERS' && (
          <ThemeProvider theme={importModuleTheme}>
            <ImportMarkersPanel onClose={() => handleSetActiveMenu('HOME')} />
          </ThemeProvider>
        )}

        <Box
          component='main'
          sx={{
            gridArea: 'content',
            position: 'relative',
            overflow: isMapRoute ? 'hidden' : 'auto',
            zIndex: 1,
            minWidth: 0,
            minHeight: 0,
          }}>
          {activeMenu === 'HOME' && <Home selectedMarker={selectedMarkerData} />}
          {activeMenu === 'NEWS' && <NewsCarousel />}
          {activeMenu === 'VIEW_INFO' && (
            <ViewInfo
              selectedMarker={selectedMarkerData}
              onDeselect={() => {
                setSelectedMarkerData(null);
                handleAction('CLOSE_DRAWER');
              }}
            />
          )}

          {['MARKUP', 'MARKUP_MARKERS', 'MARKUP_INFO'].includes(activeMenu) && (
            <CustomDrawer
              key={activeMenu}
              activeMenu={activeMenu}
              setActiveMenu={handleSetActiveMenu}
              activeTool={activeTool}
              setActiveTool={setActiveTool}
              onAction={handleAction}
              drawingStyles={drawingStyles}
              setDrawingStyles={setDrawingStyles}
              showPost={showPost}
              onClosePost={() => setShowPost(false)}
              editPost={editPost}
              onCloseEdit={() => setEditPost(false)}
              eventDetails={eventDetails}
              setEventsDetails={setEventsDetails}
              selectedMarker={selectedMarkerData}
              updateMarkerInState={updateMarkerInState}
              setIsDrawerEditing={setIsDrawerEditing}
              onDeselect={() => {
                if (selectionLockRef.current) return;
                setSelectedMarkerData(null);
                handleAction('CLOSE_DRAWER');
              }}
              isUIDirty={isUIDirty}
              setIsUIDirty={setIsUIDirty}
              closeTrigger={closeTrigger}
            />
          )}

          <Routes>
            <Route
              path='/fullscreen'
              element={
                <Dashboard
                  activeMenu={activeMenu}
                  setActiveMenu={handleSetActiveMenu}
                  setEditPost={setEditPost}
                  setEventsDetails={setEventsDetails}
                  activeTool={activeTool}
                  currentAction={currentAction}
                  drawingStyles={drawingStyles}
                  setDrawingStyles={setDrawingStyles}
                  setSelectedMarkerData={handleMarkerSelectionRequest}
                  updateMarkerInState={updateMarkerInState}
                  isDrawerEditing={isDashboardEditing}
                  hasUnsavedChanges={isUIDirty}
                  onSelectionBlocked={handleSelectionBlocked}
                  selectedMarker={selectedMarkerData}
                  setGetMapCurrentState={setGetMapCurrentState}
                  onMapLoad={handleMapLoad}
                  filterCriteria={filterCriteria}
                  onFilterResults={handleFilterResults}
                  searchMarker={searchMarker}
                  setSearchMarker={handleSetSearchMarker}
                />
              }
            />

            <Route
              path='/info-fullscreen'
              element={
                <Dashboard
                  activeMenu={activeMenu}
                  setActiveMenu={handleSetActiveMenu}
                  setEditPost={setEditPost}
                  setEventsDetails={setEventsDetails}
                  activeTool={activeTool}
                  currentAction={currentAction}
                  drawingStyles={drawingStyles}
                  setDrawingStyles={setDrawingStyles}
                  setSelectedMarkerData={handleMarkerSelectionRequest}
                  updateMarkerInState={updateMarkerInState}
                  isDrawerEditing={isDashboardEditing}
                  hasUnsavedChanges={isUIDirty}
                  onSelectionBlocked={handleSelectionBlocked}
                  selectedMarker={selectedMarkerData}
                  setGetMapCurrentState={setGetMapCurrentState}
                  onMapLoad={handleMapLoad}
                  filterCriteria={filterCriteria}
                  onFilterResults={handleFilterResults}
                  searchMarker={searchMarker}
                  setSearchMarker={handleSetSearchMarker}
                />
              }
            />

            <Route
              path='/'
              element={
                <Dashboard
                  activeMenu={activeMenu}
                  setActiveMenu={handleSetActiveMenu}
                  setEditPost={setEditPost}
                  setEventsDetails={setEventsDetails}
                  activeTool={activeTool}
                  currentAction={currentAction}
                  drawingStyles={drawingStyles}
                  setDrawingStyles={setDrawingStyles}
                  setSelectedMarkerData={handleMarkerSelectionRequest}
                  updateMarkerInState={updateMarkerInState}
                  isDrawerEditing={isDashboardEditing}
                  hasUnsavedChanges={isUIDirty}
                  onSelectionBlocked={handleSelectionBlocked}
                  selectedMarker={selectedMarkerData}
                  setGetMapCurrentState={setGetMapCurrentState}
                  onMapLoad={handleMapLoad}
                  filterCriteria={filterCriteria}
                  onFilterResults={handleFilterResults}
                  searchMarker={searchMarker}
                  setSearchMarker={handleSetSearchMarker}
                />
              }
            />
            <Route
              path='/signin'
              element={
                <Dashboard
                  activeMenu={activeMenu}
                  setActiveMenu={handleSetActiveMenu}
                  setEditPost={setEditPost}
                  setEventsDetails={setEventsDetails}
                  activeTool={activeTool}
                  currentAction={currentAction}
                  drawingStyles={drawingStyles}
                  setDrawingStyles={setDrawingStyles}
                  setSelectedMarkerData={handleMarkerSelectionRequest}
                  updateMarkerInState={updateMarkerInState}
                  isDrawerEditing={isDashboardEditing}
                  hasUnsavedChanges={isUIDirty}
                  onSelectionBlocked={handleSelectionBlocked}
                  selectedMarker={selectedMarkerData}
                  setGetMapCurrentState={setGetMapCurrentState}
                  onMapLoad={handleMapLoad}
                  filterCriteria={filterCriteria}
                  onFilterResults={handleFilterResults}
                  searchMarker={searchMarker}
                  setSearchMarker={handleSetSearchMarker}
                />
              }
            />
            <Route
              path='/signup'
              element={
                <Dashboard
                  activeMenu={activeMenu}
                  setActiveMenu={handleSetActiveMenu}
                  setEditPost={setEditPost}
                  setEventsDetails={setEventsDetails}
                  activeTool={activeTool}
                  currentAction={currentAction}
                  drawingStyles={drawingStyles}
                  setDrawingStyles={setDrawingStyles}
                  setSelectedMarkerData={handleMarkerSelectionRequest}
                  updateMarkerInState={updateMarkerInState}
                  isDrawerEditing={isDashboardEditing}
                  hasUnsavedChanges={isUIDirty}
                  onSelectionBlocked={handleSelectionBlocked}
                  selectedMarker={selectedMarkerData}
                  setGetMapCurrentState={setGetMapCurrentState}
                  onMapLoad={handleMapLoad}
                  filterCriteria={filterCriteria}
                  onFilterResults={handleFilterResults}
                  searchMarker={searchMarker}
                  setSearchMarker={handleSetSearchMarker}
                />
              }
            />
            <Route
              path='/verify-email'
              element={
                <Dashboard
                  activeMenu={activeMenu}
                  setActiveMenu={handleSetActiveMenu}
                  setEditPost={setEditPost}
                  setEventsDetails={setEventsDetails}
                  activeTool={activeTool}
                  currentAction={currentAction}
                  drawingStyles={drawingStyles}
                  setDrawingStyles={setDrawingStyles}
                  setSelectedMarkerData={handleMarkerSelectionRequest}
                  updateMarkerInState={updateMarkerInState}
                  isDrawerEditing={isDashboardEditing}
                  hasUnsavedChanges={isUIDirty}
                  onSelectionBlocked={handleSelectionBlocked}
                  selectedMarker={selectedMarkerData}
                  setGetMapCurrentState={setGetMapCurrentState}
                  onMapLoad={handleMapLoad}
                  filterCriteria={filterCriteria}
                  onFilterResults={handleFilterResults}
                  searchMarker={searchMarker}
                  setSearchMarker={handleSetSearchMarker}
                />
              }
            />

            <Route
              path='/change-password'
              element={
                auth.currentUser ? (
                  <>
                    <Dashboard
                      activeMenu={activeMenu}
                      setActiveMenu={handleSetActiveMenu}
                      setEditPost={setEditPost}
                      setEventsDetails={setEventsDetails}
                      activeTool={activeTool}
                      currentAction={currentAction}
                      drawingStyles={drawingStyles}
                      setDrawingStyles={setDrawingStyles}
                      setSelectedMarkerData={handleMarkerSelectionRequest}
                      updateMarkerInState={updateMarkerInState}
                      isDrawerEditing={isDashboardEditing}
                      hasUnsavedChanges={isUIDirty}
                      onSelectionBlocked={handleSelectionBlocked}
                      selectedMarker={selectedMarkerData}
                      setGetMapCurrentState={setGetMapCurrentState}
                      onMapLoad={handleMapLoad}
                      filterCriteria={filterCriteria}
                      onFilterResults={handleFilterResults}
                      searchMarker={searchMarker}
                      setSearchMarker={handleSetSearchMarker}
                    />
                    <ChangePassword />
                  </>
                ) : (
                  <Navigate to='/' replace />
                )
              }
            />
            <Route path='*' element={<Navigate to='/' replace />} />
          </Routes>
        </Box>
      </Box>

      {/* --- CONDITIONAL AUTH MODALS --- */}
      {authModal === 'LOGIN' && (
        <Login
          open={true}
          onClose={handleAuthModalClose}
          onSwitchToSignup={() => navigate('/signup')}
        />
      )}

      {authModal === 'SIGNUP' && (
        <Signup
          open={true}
          onClose={handleAuthModalClose}
          onSwitchToLogin={() => navigate('/signin')}
          onSuccess={(data) => {
            setLinkingData(data || null);
            navigate('/verify-email');
          }}
        />
      )}

      {authModal === 'VERIFY' && (
        <VerifyEmail
          open={true}
          onClose={handleAuthModalClose}
          linkingData={linkingData}
        />
      )}

      {alert.visible && (
        <Box
          sx={{
            position: 'fixed',
            top: '146px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'auto',
            zIndex: 100010,
            pointerEvents: 'none',
          }}>
          <Box sx={{ pointerEvents: 'auto', minWidth: '300px' }}>
            <AlertBox visible={true} severity={alert.severity} message={alert.message} />
          </Box>
        </Box>
      )}

      <SearchConversionHandler
        open={showSearchConvertDialog}
        onClose={handleConvertSearchNo}
        searchData={tempSearchData}
        onSuccess={handleConversionSuccess}
        onError={() => showAlert('error', 'Failed to create marker')}
      />

      {/* FULLSCREEN OVERLAYS */}
      {isFullscreenRoute && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 100000,
            backgroundColor: '#fff',
          }}>
          <FullscreenPage
            logoUrl={mainLogoUrl}
            onSyncMarker={(marker) => {
              if (marker) {
                handleSetSearchMarker(marker);
              }
            }}
          />
        </Box>
      )}

      {isInfoFullscreenRoute && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 100000,
            backgroundColor: '#fff',
          }}>
          <ViewInfoFullPage logoUrl={mainLogoUrl} />
        </Box>
      )}
    </>
  );
}

function App() {
  useEffect(() => {
    document.title = process.env.REACT_APP_TAB_TITLE || 'loKtr';
  }, []);

  return (
    <MapProvider>
      <IconProvider>
        <InnerApp />
      </IconProvider>
    </MapProvider>
  );
}

export default App;
